import {
  buildDateFilterSpecs,
  buildNumberFilterSpecs,
  buildValueFilterSpec,
} from "./filterSpecUtils";
import { humanizeKey } from "common/strings";

export const DASHBOARD_REPORT_ID = "dashboard_report";

const DASHBOARD_DISPLAY_NAME = "Submission Status";
const DASHBOARD_DESCRIPTION = "Submission status – same columns as Review";

const HIDDEN_COLUMNS = ["submissionId", "layerId"];

const DEFAULT_REVIEW_COLUMNS = [
  { key: "inception" },
  { key: "expiration" },
  { key: "insured" },
  { key: "transaction" },
  { key: "industryClass" },
  { key: "preferredUnderwriter", title: "Underwriter" },
  { key: "brokerHouse", title: "Broking House" },
  { key: "broker" },
  { key: "status" },
  { key: "premium" },
  { key: "brokeragePremium", title: "Brokerage" },
  { key: "limit" },
  { key: "attachment" },
  { key: "layerReference", title: "Reference" },
  { key: "targetPrice" },
];

const DEFAULT_ORDER = [{ column: "inception", direction: "DESC" }];

/**
 * Build reportsV3 filter-spec entries ({ column, function, parameters }) from
 * the per-company `submission_review_config.reviewFilters.default` list. This is
 * how a company seeds the filters shown by default (e.g. Date Received). Each
 * config entry is one of:
 *   { column, values: [...] }       -> IN (enum / string)
 *   { column, from, to }            -> date range (>= / <=)
 *   { column, withinDays: N }       -> relative date range today..today+N
 *                                      (e.g. "renewing soon" on `expiration`)
 *   { column, min, max }            -> numeric range (>= / <=)
 *   { column, boolean: true|false } -> IS_TRUE / IS_FALSE
 * Only columns that exist on the table (knownColumnNames) are kept.
 */
const buildDefaultFilterSpecs = (defaultFilters, knownColumnNames) => {
  const specs = [];
  (defaultFilters ?? []).forEach((entry) => {
    const column = entry?.column;
    if (!column || !knownColumnNames.has(column)) return;
    if (Array.isArray(entry.values) && entry.values.length > 0) {
      specs.push(buildValueFilterSpec(column, entry.values));
    }
    specs.push(
      ...buildDateFilterSpecs(column, { from: entry.from, to: entry.to })
    );
    if (typeof entry.withinDays === "number") {
      // Relative date window today..today+N — used for "renewing soon" on the
      // `expiration` column (parity with the legacy expiring filter). Filter the
      // real date column, NOT the synthesized daysToExpiry (which doesn't exist
      // in the materialized query and would be silently ignored).
      const today = new Date();
      const upper = new Date(today);
      upper.setDate(upper.getDate() + entry.withinDays);
      specs.push(...buildDateFilterSpecs(column, { from: today, to: upper }));
    }
    specs.push(
      ...buildNumberFilterSpecs(column, { min: entry.min, max: entry.max })
    );
    if (typeof entry.boolean === "boolean") {
      specs.push(buildValueFilterSpec(column, [String(entry.boolean)], true));
    }
  });
  return specs;
};

/**
 * Resolve the default sort order from `reviewFilters.defaultSort`
 * ({ column, direction }), falling back to inception DESC. Lets a company open
 * the queue sorted by e.g. Date Received to surface new submissions.
 */
const resolveOrder = (defaultSort, knownColumnNames) => {
  if (!defaultSort?.column || !knownColumnNames.has(defaultSort.column)) {
    return DEFAULT_ORDER;
  }
  const direction = defaultSort.direction === "ASC" ? "ASC" : "DESC";
  return [{ column: defaultSort.column, direction }];
};

// Resolve a human-readable column header: explicit config title wins, then the
// backend's display name (so "bi" -> "BI", "statusReason" -> "Decline/NTU
// Reason"), then a humanized fallback. Never shows the raw camelCase key.
const resolveDisplayName = (key, title, displayNameMap) =>
  title ?? displayNameMap?.[key] ?? humanizeKey(key);

export const buildSubmissionStatusSpecification = (
  columnConfig,
  knownColumnNames,
  displayNameMap = {}
) => {
  const list =
    (columnConfig?.reviewColumns?.length && columnConfig.reviewColumns) ||
    DEFAULT_REVIEW_COLUMNS;
  const seen = new Set();
  const columns = list.filter((spec) => {
    if (seen.has(spec.key)) return false;
    seen.add(spec.key);
    return knownColumnNames.has(spec.key);
  });
  const visibleKeys = new Set(columns.map((c) => c.key));
  const activeHiddenColumns = HIDDEN_COLUMNS.filter((col) =>
    knownColumnNames.has(col)
  );

  const filterConfig = columnConfig?.reviewFilters ?? {};
  const defaultFilters = buildDefaultFilterSpecs(
    filterConfig.default,
    knownColumnNames
  );

  // Columns referenced by a default filter that aren't displayed are pulled in
  // as hidden (notForExport) columns. This (a) keeps the backend from dropping
  // the filter — it strips filters on unknown source columns (see
  // reports_v3/dtos.py:_strip_unknown_column_refs) — and (b) surfaces the filter
  // in the Filters chooser so users can toggle it on/off (e.g. a "renewing soon"
  // filter on daysToExpiry / isNonRenewing) without displaying the column.
  const filterColumns = [
    ...new Set(defaultFilters.map((f) => f.column)),
  ].filter(
    (col) => !visibleKeys.has(col) && !activeHiddenColumns.includes(col)
  );

  const hiddenColumns = [...activeHiddenColumns, ...filterColumns];
  const sourceColumnNames = new Set([...visibleKeys, ...hiddenColumns]);

  const tableName = "review_list";
  const sourceColumns = Array.from(sourceColumnNames).map((internalName) => ({
    sourceTable: tableName,
    sourceColumn: internalName,
    column: internalName,
    displayName: resolveDisplayName(internalName, undefined, displayNameMap),
  }));
  const HIDDEN_DISPLAY_NAMES = {
    submissionId: "Submission ID",
    layerId: "Layer ID",
  };
  const outputColumns = [
    ...columns.map((spec) => ({
      column: spec.key,
      displayName: resolveDisplayName(spec.key, spec.title, displayNameMap),
    })),
    ...hiddenColumns.map((col) => ({
      column: col,
      displayName:
        HIDDEN_DISPLAY_NAMES[col] ??
        resolveDisplayName(col, undefined, displayNameMap),
      notForExport: true,
    })),
  ];

  return {
    sourceTables: [tableName],
    sourceColumns,
    filters: defaultFilters,
    order: resolveOrder(filterConfig.defaultSort, knownColumnNames),
    outputColumns,
    aggregate: null,
    joins: null,
    group: null,
  };
};

export const INITIAL_METRICS = [
  { method: "COUNT_UNIQUE", key: "submissionId", label: "Submissions" },
  { method: "COUNT_UNIQUE", key: "layerId", label: "Layers" },
  {
    method: "COUNT_UNIQUE",
    key: "status",
    label: "Status",
    chartType: "enumButtons",
    statusChart: true,
  },
];

// Financial summary cards shown by default, mirroring the legacy Review tab
// (ReviewApp.js defaultDashboardSummaryConfig). The v3 metrics panel consumes
// this shape directly ({ method, key, label, prefix }) via
// MetricsButton -> aggregates.aggregatedByKey, and is expanded by default.
const DEFAULT_REVIEW_METRICS = [
  { method: "MAX", key: "limit", label: "Largest Single Limit", prefix: "$" },
  { method: "SUM", key: "limit", label: "Total Limit", prefix: "$" },
  { method: "SUM", key: "premium", label: "Total GWP", prefix: "$" },
  {
    method: "SUM",
    key: "brokeragePremium",
    label: "Total Brokerage",
    prefix: "$",
  },
];

/**
 * Resolve the dashboard metrics from `submission_review_config.reviewMetrics`.
 * An explicit array is used verbatim; an absent value (or the legacy "DEFAULT"
 * sentinel) falls back to the financial summary cards.
 */
export const resolveReviewMetrics = (reviewMetrics) => {
  const metrics = Array.isArray(reviewMetrics)
    ? reviewMetrics
    : DEFAULT_REVIEW_METRICS;
  // The v3 metrics panel has no per-metric filter support, so a legacy metric
  // carrying `filters` would aggregate over ALL rows and show a misleading
  // total — drop those rather than display a wrong number.
  return metrics.filter((m) => m && !m.filters);
};

/**
 * Metrics are computed client-side from the report rows, so each metric's key
 * column must be present in the output (added as a hidden column if it isn't
 * already displayed), otherwise the metric can't be calculated.
 */
const withMetricColumns = (
  specification,
  metrics,
  knownColumnNames,
  displayNameMap = {}
) => {
  const outputCols = new Set(specification.outputColumns.map((c) => c.column));
  const sourceCols = new Set(specification.sourceColumns.map((c) => c.column));
  const missing = [
    ...new Set(metrics.map((m) => m.key).filter(Boolean)),
  ].filter((key) => knownColumnNames.has(key) && !outputCols.has(key));
  if (missing.length === 0) return specification;
  const tableName = specification.sourceTables[0];
  return {
    ...specification,
    sourceColumns: [
      ...specification.sourceColumns,
      ...missing
        .filter((key) => !sourceCols.has(key))
        .map((key) => ({
          sourceTable: tableName,
          sourceColumn: key,
          column: key,
          displayName: resolveDisplayName(key, undefined, displayNameMap),
        })),
    ],
    outputColumns: [
      ...specification.outputColumns,
      ...missing.map((key) => ({
        column: key,
        displayName: resolveDisplayName(key, undefined, displayNameMap),
        notForExport: true,
      })),
    ],
  };
};

/**
 * Resolve the config the v3 dashboard should use. A company can carry a `v3`
 * block inside submission_review_config to migrate to the v3 schema without
 * touching the v2 config the legacy Review tab still reads. Keys set in `v3`
 * override the top-level (v2) ones; anything omitted falls back to v2. A company
 * not yet "updated to v3" (no `v3` block) therefore renders v3 from the v2
 * schema. The legacy tab always reads the top-level keys, so it is unaffected.
 */
export const resolveV3Config = (columnConfig) =>
  columnConfig?.v3 ? { ...columnConfig, ...columnConfig.v3 } : columnConfig;

export const buildDashboardReportConfig = (
  columnConfig,
  knownColumnNames,
  displayNameMap = {}
) => {
  const cfg = resolveV3Config(columnConfig);
  const metrics = resolveReviewMetrics(cfg?.reviewMetrics);
  const specification = withMetricColumns(
    buildSubmissionStatusSpecification(cfg, knownColumnNames, displayNameMap),
    metrics,
    knownColumnNames,
    displayNameMap
  );
  return {
    internalName: DASHBOARD_REPORT_ID,
    displayName: DASHBOARD_DISPLAY_NAME,
    description: DASHBOARD_DESCRIPTION,
    type: "SINGLE_TABLE_REPORT",
    readOnly: true,
    canWrite: false,
    allUsers: true,
    specification,
    configuration: { metrics },
  };
};

// Strip every reference to a no-longer-valid column from a spec, so a saved view
// that references a removed/renamed column keeps its remaining columns, filters,
// sort and grouping instead of being thrown away wholesale. Never mutates input.
const pruneSpecColumns = (spec, validColumns) => {
  const ok = (col) => !col || validColumns.has(col);
  const pruned = { ...spec };
  if (Array.isArray(spec.sourceColumns)) {
    pruned.sourceColumns = spec.sourceColumns.filter((c) => ok(c.column));
  }
  if (Array.isArray(spec.outputColumns)) {
    pruned.outputColumns = spec.outputColumns.filter((c) => ok(c.column));
  }
  if (Array.isArray(spec.filters)) {
    pruned.filters = spec.filters.filter((f) => ok(f.column));
  }
  if (Array.isArray(spec.preAggregationFilters)) {
    const f = spec.preAggregationFilters.filter((x) => ok(x.column));
    pruned.preAggregationFilters = f.length ? f : null;
  }
  if (Array.isArray(spec.order)) {
    pruned.order = spec.order.filter((o) => ok(o.column));
  }
  if (Array.isArray(spec.aggregate)) {
    const a = spec.aggregate.filter((x) => ok(x.column));
    pruned.aggregate = a.length ? a : null;
  }
  if (Array.isArray(spec.group) && spec.group.length) {
    const g = spec.group[0];
    const groupColumns = (g.groupColumns ?? []).filter((col) =>
      validColumns.has(col)
    );
    // If a grouped-on column went away, drop grouping rather than ship an
    // invalid group; otherwise keep it with any dead aggregates removed.
    pruned.group =
      groupColumns.length === (g.groupColumns ?? []).length
        ? [{ ...g, aggregate: (g.aggregate ?? []).filter((x) => ok(x.column)) }]
        : null;
  }
  if (spec.summaryRow?.aggregations) {
    const aggregations = spec.summaryRow.aggregations.filter((x) =>
      ok(x.column)
    );
    pruned.summaryRow = aggregations.length
      ? { ...spec.summaryRow, aggregations }
      : null;
  }
  return pruned;
};

// Make every column in the current config available in an (older) saved view by
// appending any that the view lacks as HIDDEN (notForExport) output + source
// columns. They don't change what the view displays, but they show up in the
// column/filter chooser so a renamed/newly-added column (e.g. brokerLocation
// replacing brokerHouseLocation) can be turned on. Never mutates the input.
const addAvailableColumns = (spec, defaultSpec) => {
  if (!defaultSpec) return spec;
  const haveOut = new Set((spec.outputColumns ?? []).map((c) => c.column));
  const haveSrc = new Set((spec.sourceColumns ?? []).map((c) => c.column));
  const extraOut = (defaultSpec.outputColumns ?? [])
    .filter((c) => c.column && !haveOut.has(c.column))
    .map((c) => ({ ...c, notForExport: true }));
  const extraSrc = (defaultSpec.sourceColumns ?? []).filter(
    (c) => c.column && !haveSrc.has(c.column)
  );
  if (!extraOut.length && !extraSrc.length) return spec;
  return {
    ...spec,
    outputColumns: [...(spec.outputColumns ?? []), ...extraOut],
    sourceColumns: [...(spec.sourceColumns ?? []), ...extraSrc],
  };
};

// Metrics are computed client-side from the fetched rows, so every metric's key
// column must be present in the spec — otherwise the metric shows blank. A saved
// view can carry metrics on columns the current config no longer displays (e.g.
// metrics on `limit`/`premium` after the config moved to `lineSize`/
// `shareOfPremium`); those columns get pruned as "stale" and only the metrics
// whose key happens to still be a config column keep working. Re-add any metric
// key column that's missing as a HIDDEN (notForExport) output + source column.
// Genuinely-unknown columns are stripped server-side, so this is safe. Never
// mutates the input.
export const ensureMetricColumns = (spec, metrics) => {
  const keys = [...new Set((metrics ?? []).map((m) => m?.key).filter(Boolean))];
  if (!keys.length) return spec;
  const haveOut = new Set((spec.outputColumns ?? []).map((c) => c.column));
  const haveSrc = new Set((spec.sourceColumns ?? []).map((c) => c.column));
  const missingOut = keys.filter((k) => !haveOut.has(k));
  const missingSrc = keys.filter((k) => !haveSrc.has(k));
  if (!missingOut.length && !missingSrc.length) return spec;
  const tableName = spec.sourceTables?.[0] ?? "review_list";
  return {
    ...spec,
    outputColumns: [
      ...(spec.outputColumns ?? []),
      ...missingOut.map((k) => ({
        column: k,
        displayName: resolveDisplayName(k, undefined, {}),
        notForExport: true,
      })),
    ],
    sourceColumns: [
      ...(spec.sourceColumns ?? []),
      ...missingSrc.map((k) => ({
        sourceTable: tableName,
        sourceColumn: k,
        column: k,
        displayName: resolveDisplayName(k, undefined, {}),
      })),
    ],
  };
};

/**
 * Merge a user's locally-saved layout over the pristine, config-driven dashboard
 * report so the saved view (columns / filters / sort / grouping / summary /
 * metrics) is what loads and executes. If the saved layout references columns
 * that no longer exist (the company config or backend columns changed since it
 * was saved), those columns are *pruned* — the rest of the view is preserved —
 * and their display names are returned on `staleColumns` so the UI can tell the
 * user. Only when nothing usable survives do we fall back to the default (still
 * flagging the dropped columns). Never mutates the input.
 */
export const mergeSavedLayout = (defaultReport, savedLayout, knownColumns) => {
  const savedSpec = savedLayout?.specification;
  if (!defaultReport || !savedSpec) return defaultReport;

  // A saved column is stale only if it isn't a real table column. Judge against
  // the full review_list column set (knownColumns) — NOT the config's default
  // columns — so a valid column a user added to their view (e.g. "Our Limit")
  // isn't falsely pruned just because it isn't in the company default. Fall back
  // to the config columns only when the known set is unavailable.
  const validColumns =
    knownColumns instanceof Set && knownColumns.size > 0
      ? knownColumns
      : new Set(
          (defaultReport.specification?.sourceColumns ?? []).map(
            (c) => c.column
          )
        );

  // Columns the saved view shows/sources that are no longer available. Tracked
  // by display name (for the user message) and by key (`staleColumnKeys`, so the
  // loaded view can be flagged as having unsaved changes — see buildTabEntry).
  const staleColumns = [];
  const staleColumnKeys = [];
  const seen = new Set();
  for (const c of [
    ...(savedSpec.outputColumns ?? []),
    ...(savedSpec.sourceColumns ?? []),
  ]) {
    if (!c.column || validColumns.has(c.column) || seen.has(c.column)) continue;
    seen.add(c.column);
    staleColumns.push(c.displayName || c.column);
    staleColumnKeys.push(c.column);
  }
  const staleInfo = staleColumns.length
    ? { staleColumns, staleColumnKeys }
    : {};

  // Prune any removed columns, then ALWAYS sync the view with the current config:
  // add any config column the (possibly older) saved view lacks as hidden/
  // available, so newly-added or renamed columns (e.g. brokerLocation) always
  // show up in the column/filter chooser — even for views saved before them.
  const prunedSpec = staleColumns.length
    ? pruneSpecColumns(savedSpec, validColumns)
    : savedSpec;
  const withAvailable = addAvailableColumns(
    prunedSpec,
    defaultReport.specification
  );

  // The config is the source of truth for metrics: config metrics SUPERSEDE the
  // saved view's stored metrics, so a config change (or an outdated saved view)
  // always resolves to the current definitions. A saved view only contributes its
  // column/filter/sort layout — not stale metric keys. Their key columns are
  // pulled in (hidden) so the cards still compute even when not displayed.
  const metrics = defaultReport.configuration?.metrics;
  const spec = ensureMetricColumns(withAvailable, metrics);

  // Nothing visible survived the prune (or the saved view had no columns) — load
  // the company default, but still surface which columns went away.
  const hasVisibleOutput = (spec.outputColumns ?? []).some(
    (c) => !c.notForExport
  );
  if (!hasVisibleOutput) {
    return staleColumns.length
      ? { ...defaultReport, ...staleInfo }
      : defaultReport;
  }

  return {
    ...defaultReport,
    specification: spec,
    configuration: { ...defaultReport.configuration },
    ...staleInfo,
  };
};

export const DASHBOARD_REPORT_STUB = {
  internalName: DASHBOARD_REPORT_ID,
  displayName: DASHBOARD_DISPLAY_NAME,
  description: DASHBOARD_DESCRIPTION,
  type: "SINGLE_TABLE_REPORT",
  readOnly: true,
  canWrite: false,
  allUsers: true,
  created: "2000-01-01T00:00:00.000Z",
};
